import {
  Filter,
  getA2APaymentById,
  getCardPaymentById,
  LoanStatus,
  SinglePaymentStatus,
} from '@nemuru-eng/js-api-client';
import { generatePath, useNavigate } from 'react-router-dom';

import { SinglePaymentStatusLabel } from '@/components/modules/SinglePaymentListByProduct/components/SinglePaymentStatusLabel';
import { ALLOWED_SINGLE_PAYMENT_STATUSES_FOR_MULTI_SELECT_FILTER } from '@/components/modules/SinglePaymentListByProduct/constants/constants';
import { useFetchSinglePaymentList } from '@/components/modules/SinglePaymentListByProduct/hooks/useFetchSinglePaymentList';
import { SinglePaymentDataGridFields } from '@/components/modules/SinglePaymentListByProduct/utils/SinglePaymentDataGridFields/SinglePaymentDataGridFields';
import { MultiFilteredList } from '@/components/shared/components/MultiFilteredList';
import { Text } from '@/components/shared/components/Text';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { ApplicationType, SinglePaymentApplicationType } from '@/config/types';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useCache } from '@/hooks/requests/useCache';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

interface SinglePaymentListByApplicationTypeProps {
  applicationType: SinglePaymentApplicationType;
  listProps?: {
    additionalFilters?: Filter[];
  };
}

export const SinglePaymentListByApplicationType = ({
  applicationType,
  listProps,
}: SinglePaymentListByApplicationTypeProps) => {
  const {
    state: { lastViewedSinglePaymentId },
    actions: { updateOperationsContext },
  } = useOperationsContext();

  const singlePayment = lastViewedSinglePaymentId
    ? applicationType === ApplicationType.A2A_PAYMENT
      ? useCache(getA2APaymentById, { id: lastViewedSinglePaymentId })?.data
      : useCache(getCardPaymentById, { cardPaymentId: lastViewedSinglePaymentId })?.data
    : undefined;

  const navigate = useNavigate();
  const { fetchSinglePaymentList } = useFetchSinglePaymentList({
    applicationType,
    additionalFilters: listProps?.additionalFilters,
  });

  const itemsValues = ALLOWED_SINGLE_PAYMENT_STATUSES_FOR_MULTI_SELECT_FILTER;

  const getDetailPath = () => {
    switch (applicationType) {
      case ApplicationType.A2A_PAYMENT:
        return DYNAMIC_PAGE_PATHS.A2A_PAYMENT;
      case ApplicationType.CARD_PAYMENT:
        return DYNAMIC_PAGE_PATHS.CARD_PAYMENT;
    }
  };
  const tableColumns = SinglePaymentDataGridFields({ applicationType });

  const detailPath = getDetailPath();

  return (
    <MultiFilteredList
      fetchList={fetchSinglePaymentList}
      dataGridFields={tableColumns}
      onRowClick={(selectedRow) => {
        updateOperationsContext({ lastViewedSinglePaymentId: selectedRow.id });
        detailPath && navigate(generatePath(detailPath, { paymentMethodId: selectedRow.id }));
      }}
      getRowClassName={(params) => {
        if (params.row.id === singlePayment?.id) return 'last-viewed-application';
        else return '';
      }}
      multiselectProps={{
        field: 'status',
        label: <Text tId={'STATUS'} />,
        defaultLabel: <Text tId={'DEFAULT_STATUSES'} />,
        items: itemsValues,
        checkBoxRender: (value) => {
          return <SinglePaymentStatusLabel status={value} />;
        },
        filtersValueHandler: (valueArray: string[]) => {
          if (valueArray?.includes(LoanStatus.STATUS_ACTIVE)) {
            valueArray.push(LoanStatus.STATUS_ACTIVE_TRIAL);
          }
          if (valueArray?.includes(LoanStatus.STATUS_CANCELLED)) {
            valueArray.push(LoanStatus.STATUS_CANCELLED_TEST);
          }
          return valueArray;
        },
        defaultItems: [
          SinglePaymentStatus.CONFIRMED,
          SinglePaymentStatus.PENDING,
          SinglePaymentStatus.PROCESSING,
          SinglePaymentStatus.CANCELLED,
          SinglePaymentStatus.DENIED,
        ],
      }}
      dateFilterProps={{
        dateField: 'updated_at',
      }}
      defaultOrderBy={'updated_at'}
      showExport={false}
      disableSelectionOnClick
    />
  );
};

export const SinglePaymentListByApplicationTypeModule = withPermissionsModule(SinglePaymentListByApplicationType);
